import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import { useNavigate } from "react-router-dom";
import { RootState, GameData } from "types";

import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import {
  DeleteOutline,
  InfoOutlined,
  MessageOutlined,
} from "@mui/icons-material";

import DB from "_DatabaseFunctions/directConnect";

function Waitlists() {
  const user = useSelector((state: RootState) => state.user.user);
  const waitlists = useSelector((state: RootState) => state.games.playerWaitlist);
  const navigate = useNavigate();

  const [positions, setPositions] = useState<Record<string, string>>();

  useEffect(() => {
    setPositions(calculatePositions());
  }, [waitlists]);

  // Todo: Calculate on server
  const handleLeaveWaitlist = async (gameData: GameData) => {
    if (!user || !gameData) return;
    DB.removeFromWaitlist(gameData.id, user.uid);
  };

  const calculatePositions = () => {
    if (waitlists) {
      const place: Record<string, string> = {};
      const keys = Object.keys(waitlists);
      keys.map((key) => {
        const players = waitlists[key].waitlist;
        const myTime = players[user?.uid]?.timeStamp;
        const pIds = Object.keys(players);
        const times = pIds.map((pId) => players[pId].timeStamp);
        times.sort();
        const pos = `${times.indexOf(myTime) + 1} of ${times.length}`;
        place[key] = pos;
      });
      return place;
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const renderWaitlistBody = () => {
    const games = Object.keys(waitlists);
    return games.map((game, index) => {
      const gameData = waitlists[game];
      return (
        <TableRow key={gameData.id}>
          <TableCell
            sx={{
              cursor: 'pointer',
              '&:hover': {
                color: 'primary.main',
                textDecoration: 'underline',
              },
            }}
            onClick={() => navigate(`/games/${gameData.id}`)}
          >
            <Typography variant="body2" fontWeight="medium">
              {gameData.gameName}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2">
              {positions?.[gameData.id]}
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Tooltip title="Message Host">
                <span>
                  <IconButton
                    onClick={() => navigate(`/messages/${gameData.hostId}`)}
                    size="small"
                    sx={{
                      bgcolor: 'info.light',
                      color: 'white',
                      width: 32,
                      height: 32,
                      '&:hover': {
                        bgcolor: 'info.main',
                      },
                    }}
                  >
                    <MessageOutlined fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              
              <Tooltip title="Leave Waitlist">
                <span>
                  <IconButton
                    onClick={() => handleLeaveWaitlist(gameData)}
                    size="small"
                    sx={{
                      bgcolor: 'error.light',
                      color: 'white',
                      width: 32,
                      height: 32,
                      '&:hover': {
                        bgcolor: 'error.main',
                      },
                    }}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              
              <Tooltip title="Game Information">
                <span>
                  <IconButton
                    onClick={() => navigate(`/games/${gameData.id}`)}
                    size="small"
                    sx={{
                      bgcolor: 'primary.light',
                      color: 'white',
                      width: 32,
                      height: 32,
                      '&:hover': {
                        bgcolor: 'primary.main',
                      },
                    }}
                  >
                    <InfoOutlined fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </TableCell>
        </TableRow>
      );
    });
  };

  if (!Object.keys(waitlists).length || !positions) {
    return null;
  }

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 2,
          mb: 2
        }}
      >
        <Typography 
          variant="h6"
          sx={{ 
            fontWeight: 600,
            color: 'text.primary'
          }}
        >
          Your Waitlists
        </Typography>
      </Box>
      
      <TableContainer
        sx={{
          maxHeight: isMobile ? 400 : 600,
          '& .MuiTableCell-root': {
            borderBottom: `1px solid ${theme.palette.divider}`,
          }
        }}
      >
        <Table 
          stickyHeader 
          size={isMobile ? 'small' : 'medium'}
          sx={{
            '& .MuiTableHead-root': {
              '& .MuiTableCell-root': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontWeight: 600,
                borderBottom: 'none'
              }
            },
            '& .MuiTableBody-root': {
              '& .MuiTableRow-root': {
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              },
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Game Name</TableCell>
              <TableCell>Position</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderWaitlistBody()}</TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default Waitlists;
